import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './WalletManagement.css';

// Base API URL - Update this to match your backend
const API_BASE_URL = 'https://trcsite.net';

function WalletManagement() {
  const [wallets, setWallets] = useState([]);
  const [groupedWallets, setGroupedWallets] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCurrency, setFilterCurrency] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [freezeModal, setFreezeModal] = useState({ open: false, wallet: null, userId: null, bulk: false });
  const [unfreezeModal, setUnfreezeModal] = useState({ open: false, wallet: null });
  const [freezeReason, setFreezeReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [viewMode, setViewMode] = useState('grouped'); // 'grouped' or 'detailed'
  const [showBackButton, setShowBackButton] = useState(false);

  const currencies = ['BTC', 'ETH', 'USDT', 'BNB', 'ALL'];
  const statusOptions = ['all', 'frozen', 'active'];

  // Create axios instance with base configuration
  const api = axios.create({
    baseURL: API_BASE_URL,
    timeout: 10000, // 10 second timeout
  });

  // Add request interceptor to include auth token
  api.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Add response interceptor to handle errors
  api.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.code === 'ECONNABORTED') {
        throw new Error('Request timeout - server is not responding');
      } else if (error.response) {
        // Server responded with error status
        throw new Error(error.response.data.message || `Server error: ${error.response.status}`);
      } else if (error.request) {
        // Network error - no response received
        throw new Error('Network error - cannot connect to server. Please check if backend is running.');
      } else {
        throw new Error('Request failed: ' + error.message);
      }
    }
  );

  useEffect(() => {
    fetchWallets();
  }, []);

  // Group wallets by user when wallets data changes
  useEffect(() => {
    if (wallets.length > 0) {
      const grouped = wallets.reduce((acc, wallet) => {
        const userId = wallet.user_id;
        if (!acc[userId]) {
          acc[userId] = {
            user_id: userId,
            user_name: wallet.user_name,
            user_email: wallet.user_email,
            uid: wallet.uid,
            wallets: [],
            total_balance: 0,
            total_locked: 0,
            frozen_wallets_count: 0,
            total_wallets: 0
          };
        }
        
        acc[userId].wallets.push(wallet);
        acc[userId].total_balance += parseFloat(wallet.balance || 0);
        acc[userId].total_locked += parseFloat(wallet.locked || 0);
        acc[userId].total_wallets += 1;
        
        if (wallet.is_frozen) {
          acc[userId].frozen_wallets_count += 1;
        }
        
        return acc;
      }, {});
      
      setGroupedWallets(grouped);
    }
  }, [wallets]);

  const fetchWallets = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found. Please login again.');
        setLoading(false);
        return;
      }

      console.log('Fetching wallets from:', `${API_BASE_URL}/api/admin/wallets`);
      
      const response = await api.get('/api/admin/wallets');
      
      if (response.data.success) {
        setWallets(response.data.wallets || []);
        console.log(`Successfully loaded ${response.data.wallets?.length || 0} wallets`);
      } else {
        throw new Error(response.data.message || 'Failed to fetch wallets');
      }
    } catch (error) {
      console.error('Error fetching wallets:', error);
      setError(error.message);
      
      // Provide specific guidance based on error type
      if (error.message.includes('Network error') || error.message.includes('timeout')) {
        setError(`
          Cannot connect to server. Please check:
          1. Backend server is running on port 5000
          2. Network connectivity
          3. CORS configuration
        `);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFreezeWallet = async () => {
    if (!freezeReason.trim()) {
      setError('Please provide a reason for freezing the wallet');
      return;
    }

    try {
      setActionLoading(true);
      setError('');
      
      if (freezeModal.bulk && freezeModal.userId) {
        // Bulk freeze all wallets for user
        await handleBulkFreeze(freezeModal.userId, true);
      } else {
        // Single wallet freeze
        const response = await api.post(
          `/api/admin/wallets/${freezeModal.wallet.id}/freeze`,
          { reason: freezeReason }
        );

        if (response.data.success) {
          alert('Wallet frozen successfully');
        } else {
          throw new Error(response.data.message || 'Failed to freeze wallet');
        }
      }
      
      setFreezeModal({ open: false, wallet: null, userId: null, bulk: false });
      setFreezeReason('');
      setShowBackButton(true);
      await fetchWallets(); // Refresh the list
    } catch (error) {
      console.error('Error freezing wallet:', error);
      setError(error.message);
      alert(`Error: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnfreezeWallet = async () => {
    try {
      setActionLoading(true);
      setError('');
      
      const response = await api.post(
        `/api/admin/wallets/${unfreezeModal.wallet.id}/unfreeze`,
        {}
      );

      if (response.data.success) {
        alert('Wallet unfrozen successfully');
        setUnfreezeModal({ open: false, wallet: null });
        setShowBackButton(true);
        await fetchWallets();
      } else {
        throw new Error(response.data.message || 'Failed to unfreeze wallet');
      }
    } catch (error) {
      console.error('Error unfreezing wallet:', error);
      setError(error.message);
      alert(`Error: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkFreeze = async (userId, freeze = true) => {
    if (freeze && !freezeReason.trim()) {
      setError('Please provide a reason for freezing wallets');
      return;
    }

    try {
      setActionLoading(true);
      setError('');

      const userWallets = groupedWallets[userId]?.wallets || [];
      const operations = userWallets.map(wallet => 
        freeze 
          ? api.post(`/api/admin/wallets/${wallet.id}/freeze`, { reason: freezeReason })
          : api.post(`/api/admin/wallets/${wallet.id}/unfreeze`, {})
      );

      const results = await Promise.allSettled(operations);
      
      const successful = results.filter(result => result.status === 'fulfilled').length;
      const failed = results.filter(result => result.status === 'rejected').length;

      alert(`${successful} wallets ${freeze ? 'frozen' : 'unfrozen'} successfully${failed > 0 ? `, ${failed} failed` : ''}`);
      
      if (freeze) {
        setFreezeReason('');
      }
      
      setShowBackButton(true);
      await fetchWallets();
    } catch (error) {
      console.error(`Error in bulk ${freeze ? 'freeze' : 'unfreeze'}:`, error);
      setError(error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const testBackendConnection = async () => {
    try {
      setError('Testing connection...');
      const response = await axios.get(`${API_BASE_URL}/api/health`, { timeout: 5000 });
      setError(`Connection successful: ${response.data.message}`);
    } catch (error) {
      setError(`Connection failed: ${error.message}`);
    }
  };

  const handleBackButton = () => {
    setShowBackButton(false);
    setSearchTerm('');
    setFilterCurrency('all');
    setFilterStatus('all');
    setViewMode('grouped');
  };

  // Filter logic for both views
  const filteredGroupedWallets = Object.values(groupedWallets).filter(userGroup => {
    const matchesSearch = 
      userGroup.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      userGroup.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      userGroup.uid?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = 
      filterStatus === 'all' || 
      (filterStatus === 'frozen' && userGroup.frozen_wallets_count > 0) ||
      (filterStatus === 'active' && userGroup.frozen_wallets_count === 0);
    
    return matchesSearch && matchesStatus;
  });

  const filteredDetailedWallets = wallets.filter(wallet => {
    const matchesSearch = 
      wallet.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      wallet.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      wallet.uid?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCurrency = filterCurrency === 'all' || wallet.currency === filterCurrency;
    const matchesStatus = 
      filterStatus === 'all' || 
      (filterStatus === 'frozen' && wallet.is_frozen) ||
      (filterStatus === 'active' && !wallet.is_frozen);
    
    return matchesSearch && matchesCurrency && matchesStatus;
  });

  const frozenWalletsCount = wallets.filter(w => w.is_frozen).length;
  const uniqueUsersCount = Object.keys(groupedWallets).length;

  if (loading) {
    return (
      <div className="wallet-management">
        <div className="loading-spinner-large"></div>
        <div className="loading-text">Loading wallet data...</div>
      </div>
    );
  }

  return (
    <div className="wallet-management">
      <div className="wallet-header">
        <div className="header-content">
          <h2>🛄 Wallet Management</h2>
          <p>Manage user wallet freeze status and monitor balances</p>
        </div>
        <div className="header-stats">
          <div className="stat-item">
            <span className="stat-label">Total Users</span>
            <span className="stat-value">{uniqueUsersCount}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Total Wallets</span>
            <span className="stat-value">{wallets.length}</span>
          </div>
          <div className="stat-item danger">
            <span className="stat-label">Frozen Wallets</span>
            <span className="stat-value">{frozenWalletsCount}</span>
          </div>
        </div>
      </div>

      {/* Connection Troubleshooting */}
      <div className="troubleshooting-section">
        {showBackButton && (
          <button 
            onClick={handleBackButton}
            className="back-button"
            title="Go back to main view"
          >
            ← Back
          </button>
        )}
      </div>

      {/* View Mode Toggle */}
      <div className="view-mode-toggle">
        <button 
          className={viewMode === 'grouped' ? 'active' : ''}
          onClick={() => setViewMode('grouped')}
        >
          👥 Grouped by User
        </button>
        <button 
          className={viewMode === 'detailed' ? 'active' : ''}
          onClick={() => setViewMode('detailed')}
        >
          📋 Detailed View
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          <div className="error-content">
            <strong>Connection Error:</strong>
            <div>{error}</div>
            <div className="error-solutions">
              <strong>Possible solutions:</strong>
              <ul>
                <li>Ensure backend server is running on port 5000</li>
                <li>Check if CORS is properly configured</li>
                <li>Verify network connectivity</li>
                <li>Check browser console for detailed errors</li>
              </ul>
            </div>
          </div>
          <button onClick={() => setError('')} className="error-close">×</button>
        </div>
      )}

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-group">
          <input
            type="text"
            placeholder="Search by user email, name, or UID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        {viewMode === 'detailed' && (
          <div className="filter-group">
            <select
              value={filterCurrency}
              onChange={(e) => setFilterCurrency(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Currencies</option>
              {currencies.map(currency => (
                <option key={currency} value={currency}>{currency}</option>
              ))}
            </select>
          </div>
        )}
        <div className="filter-group">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            {statusOptions.map(status => (
              <option key={status} value={status}>
                {status === 'all' ? 'All Status' : status === 'frozen' ? '❄️ Frozen' : '✅ Active'}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Wallets Table - Grouped View */}
      {viewMode === 'grouped' && (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>User</th>
                <th>UID</th>
                <th>Wallets</th>
                <th>Total Balance</th>
                <th>Total Locked</th>
                <th>Status</th>
                <th>Frozen Wallets</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredGroupedWallets.length > 0 ? (
                filteredGroupedWallets.map(userGroup => (
                  <tr key={userGroup.user_id} className={userGroup.frozen_wallets_count > 0 ? 'has-frozen-wallets' : ''}>
                    <td>
                      <div className="user-cell">
                        <span className="user-name">{userGroup.user_name}</span>
                        <span className="user-email">{userGroup.user_email}</span>
                      </div>
                    </td>
                    <td className="uid-cell">{userGroup.uid}</td>
                    <td className="wallets-count-cell">
                      <span className="wallets-badge">{userGroup.total_wallets}</span>
                      <div className="wallets-breakdown">
                        {userGroup.wallets.map(wallet => (
                          <div key={wallet.id} className="wallet-currency">
                            <span className={`currency-tag ${wallet.is_frozen ? 'frozen' : ''}`}>
                              {wallet.currency}
                              {wallet.is_frozen && ' ❄️'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="balance-cell">
                      {userGroup.total_balance.toFixed(8)}
                    </td>
                    <td className="locked-cell">
                      {userGroup.total_locked.toFixed(8)}
                    </td>
                    <td>
                      <span className={`status-badge ${userGroup.frozen_wallets_count > 0 ? 'status-partial-frozen' : 'status-active'}`}>
                        {userGroup.frozen_wallets_count > 0 
                          ? `❄️ ${userGroup.frozen_wallets_count}/${userGroup.total_wallets} Frozen` 
                          : '✅ All Active'}
                      </span>
                    </td>
                    <td className="frozen-count-cell">
                      {userGroup.frozen_wallets_count > 0 ? (
                        <span className="frozen-count">{userGroup.frozen_wallets_count} frozen</span>
                      ) : (
                        <span className="no-frozen">None</span>
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        {userGroup.frozen_wallets_count === 0 ? (
                          <button
                            onClick={() => {
                              setFreezeReason('');
                              setFreezeModal({ 
                                open: true, 
                                wallet: null, 
                                userId: userGroup.user_id, 
                                bulk: true 
                              });
                            }}
                            className="btn-bulk-freeze-user"
                            title="Freeze all user wallets"
                          >
                            ❄️ Freeze All
                          </button>
                        ) : userGroup.frozen_wallets_count === userGroup.total_wallets ? (
                          <button
                            onClick={() => handleBulkFreeze(userGroup.user_id, false)}
                            className="btn-bulk-unfreeze-user"
                            title="Unfreeze all user wallets"
                            disabled={actionLoading}
                          >
                            🔓 Unfreeze All
                          </button>
                        ) : (
                          <div className="mixed-actions">
                            <button
                              onClick={() => handleBulkFreeze(userGroup.user_id, false)}
                              className="btn-bulk-unfreeze-user"
                              title="Unfreeze all wallets"
                              disabled={actionLoading}
                            >
                              🔓 Unfreeze All
                            </button>
                            <button
                              onClick={() => {
                                setFreezeReason('');
                                setFreezeModal({ 
                                  open: true, 
                                  wallet: null, 
                                  userId: userGroup.user_id, 
                                  bulk: true 
                                });
                              }}
                              className="btn-bulk-freeze-user"
                              title="Freeze remaining wallets"
                            >
                              ❄️ Freeze Rest
                            </button>
                          </div>
                        )}
                        <button
                          onClick={() => {
                            setViewMode('detailed');
                            setSearchTerm(userGroup.user_email);
                          }}
                          className="btn-view-details"
                          title="View wallet details"
                        >
                          👁️ Details
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="no-data">
                    <span className="no-data-icon">🛄</span>
                    <span>{Object.keys(groupedWallets).length === 0 ? 'No users found in database' : 'No users match your filters'}</span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Wallets Table - Detailed View */}
      {viewMode === 'detailed' && (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>User</th>
                <th>UID</th>
                <th>Currency</th>
                <th>Balance</th>
                <th>Locked</th>
                <th>Status</th>
                <th>Frozen Reason</th>
                <th>Frozen Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDetailedWallets.length > 0 ? (
                filteredDetailedWallets.map(wallet => (
                  <tr key={wallet.id} className={wallet.is_frozen ? 'frozen-wallet' : ''}>
                    <td>
                      <div className="user-cell">
                        <span className="user-name">{wallet.user_name}</span>
                        <span className="user-email">{wallet.user_email}</span>
                      </div>
                    </td>
                    <td className="uid-cell">{wallet.uid}</td>
                    <td className="currency-cell">
                      <span className="currency-badge">{wallet.currency}</span>
                    </td>
                    <td className="balance-cell">
                      {parseFloat(wallet.balance || 0).toFixed(8)}
                    </td>
                    <td className="locked-cell">
                      {parseFloat(wallet.locked || 0).toFixed(8)}
                    </td>
                    <td>
                      <span className={`status-badge ${wallet.is_frozen ? 'status-frozen' : 'status-active'}`}>
                        {wallet.is_frozen ? '❄️ Frozen' : '✅ Active'}
                      </span>
                    </td>
                    <td className="reason-cell">
                      {wallet.frozen_reason || '-'}
                    </td>
                    <td className="date-cell">
                      {wallet.frozen_at ? new Date(wallet.frozen_at).toLocaleDateString() : '-'}
                    </td>
                    <td>
                      <div className="action-buttons">
                        {!wallet.is_frozen ? (
                          <button
                            onClick={() => setFreezeModal({ open: true, wallet, userId: null, bulk: false })}
                            className="btn-freeze"
                            title="Freeze Wallet"
                          >
                            ❄️ Freeze
                          </button>
                        ) : (
                          <button
                            onClick={() => setUnfreezeModal({ open: true, wallet })}
                            className="btn-unfreeze"
                            title="Unfreeze Wallet"
                          >
                            🔓 Unfreeze
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="no-data">
                    <span className="no-data-icon">🛄</span>
                    <span>{wallets.length === 0 ? 'No wallets found in database' : 'No wallets match your filters'}</span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Freeze Modal */}
      {freezeModal.open && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>
              {freezeModal.bulk ? '❄️ Freeze All Wallets' : '❄️ Freeze Wallet'}
            </h3>
            <div className="modal-body">
              <p>
                {freezeModal.bulk 
                  ? `Are you sure you want to freeze ALL wallets for this user? This will affect ${groupedWallets[freezeModal.userId]?.wallets?.length || 0} wallets.`
                  : 'Are you sure you want to freeze this wallet?'}
              </p>
              {freezeModal.bulk && freezeModal.userId && (
                <div className="user-wallets-list">
                  <strong>Wallets to be frozen:</strong>
                  <div className="wallets-grid">
                    {groupedWallets[freezeModal.userId]?.wallets.map(wallet => (
                      <div key={wallet.id} className="wallet-item">
                        <span className="currency">{wallet.currency}</span>
                        <span className="balance">{parseFloat(wallet.balance || 0).toFixed(8)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {!freezeModal.bulk && freezeModal.wallet && (
                <div className="wallet-info">
                  <strong>User:</strong> {freezeModal.wallet.user_name}<br/>
                  <strong>Currency:</strong> {freezeModal.wallet.currency}<br/>
                  <strong>Balance:</strong> {parseFloat(freezeModal.wallet.balance || 0).toFixed(8)}<br/>
                </div>
              )}
              <div className="reason-input">
                <label>Reason for freezing:</label>
                <textarea
                  value={freezeReason}
                  onChange={(e) => setFreezeReason(e.target.value)}
                  placeholder="Enter reason for freezing..."
                  rows="3"
                />
              </div>
            </div>
            <div className="modal-actions">
              <button
                onClick={() => setFreezeModal({ open: false, wallet: null, userId: null, bulk: false })}
                className="btn-cancel"
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleFreezeWallet}
                className="btn-confirm-freeze"
                disabled={actionLoading || !freezeReason.trim()}
              >
                {actionLoading 
                  ? (freezeModal.bulk ? 'Freezing All...' : 'Freezing...') 
                  : (freezeModal.bulk ? '❄️ Freeze All Wallets' : '❄️ Freeze Wallet')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unfreeze Modal */}
      {unfreezeModal.open && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>🔓 Unfreeze Wallet</h3>
            <div className="modal-body">
              <p>Are you sure you want to unfreeze this wallet?</p>
              <div className="wallet-info">
                <strong>User:</strong> {unfreezeModal.wallet.user_name} ({unfreezeModal.wallet.user_email})<br/>
                <strong>Currency:</strong> {unfreezeModal.wallet.currency}<br/>
                <strong>Balance:</strong> {parseFloat(unfreezeModal.wallet.balance || 0).toFixed(8)}<br/>
                <strong>Locked:</strong> {parseFloat(unfreezeModal.wallet.locked || 0).toFixed(8)}<br/>
                <strong>Frozen Reason:</strong> {unfreezeModal.wallet.frozen_reason}<br/>
                <strong>Frozen Since:</strong> {new Date(unfreezeModal.wallet.frozen_at).toLocaleString()}
              </div>
            </div>
            <div className="modal-actions">
              <button
                onClick={() => setUnfreezeModal({ open: false, wallet: null })}
                className="btn-cancel"
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleUnfreezeWallet}
                className="btn-confirm-unfreeze"
                disabled={actionLoading}
              >
                {actionLoading ? 'Unfreezing...' : '🔓 Unfreeze Wallet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WalletManagement;